import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Snackbar,
  Alert,
  Pagination,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ContactPhone as ContactIcon,
  ShoppingCart as SalesIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';

// Komponenty modalne
import ClientDetailsModal from './ClientDetailsModal';
import AddContactModal from './AddContactModal';
import AddPurchaseModal from './AddPurchaseModal';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

// Stałe dla statusów klientów
const CLIENT_STATUSES = {
  VIP: 'VIP',
  Regular: 'Regulalny',
  New: 'Nowy',
  Inactive: 'Nieaktywny',
  Churned: 'Utracony'
};

// Stałe dla filtrów okresów
const CONTACT_PERIODS = {
  last_7_days: 'Ostatnie 7 dni',
  last_30_days: 'Ostatnie 30 dni',
  last_90_days: 'Ostatnie 90 dni',
  no_contact_30: 'Brak kontaktu > 30 dni',
  no_contact_90: 'Brak kontaktu > 90 dni'
};

const Clients = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Stan podstawowy
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  
  // Filtry i wyszukiwanie
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: 'all',
    status: 'all',
    contactPeriod: 'all',
    purchasePeriod: 'all'
  });
  
  // Sortowanie
  const [sortBy, setSortBy] = useState('lastContactDate');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Dialogi
  const [openAddClient, setOpenAddClient] = useState(false);
  const [openEditClient, setOpenEditClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Stany dla modali
  const [openClientDetails, setOpenClientDetails] = useState(false);
  const [openAddContact, setOpenAddContact] = useState(false);
  const [openAddPurchase, setOpenAddPurchase] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Nowy klient - formularz
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    assignedTo: isAdmin ? '' : currentUser?.uid || '',
    status: 'New',
    preferences: [],
    notes: ''
  });

  useEffect(() => {
    fetchClients();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [currentUser, isAdmin, filters, sortBy, sortOrder, page]);

  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const clientsRef = collection(db, 'clients');
      let clientsQuery;

      // Budowanie zapytania w zależności od uprawnień
      if (isAdmin) {
        if (filters.assignedTo !== 'all') {
          clientsQuery = query(
            clientsRef,
            where('assignedTo', '==', filters.assignedTo),
            orderBy(sortBy, sortOrder),
            limit(20)
          );
        } else {
          clientsQuery = query(
            clientsRef,
            orderBy(sortBy, sortOrder),
            limit(20)
          );
        }
      } else {
        // Sprzedawca widzi tylko swoich klientów
        clientsQuery = query(
          clientsRef,
          where('assignedTo', '==', currentUser.uid),
          orderBy(sortBy, sortOrder),
          limit(20)
        );
      }

      const clientsSnapshot = await getDocs(clientsQuery);
      let clientsList = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        lastContactDate: doc.data().lastContactDate?.toDate(),
        lastPurchaseDate: doc.data().lastPurchaseDate?.toDate()
      }));

      // Zastosuj filtry
      clientsList = applyClientFilters(clientsList);
      
      setClients(clientsList);
      setTotalClients(clientsList.length);
      setTotalPages(Math.ceil(clientsList.length / 20));
    } catch (error) {
      console.error('Błąd podczas pobierania klientów:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania danych klientów',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyClientFilters = (clientsList) => {
    return clientsList.filter(client => {
      // Filtr wyszukiwania
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
        const email = (client.email || '').toLowerCase();
        const phone = (client.phone || '').toLowerCase();
        
        if (!fullName.includes(searchTerm) && 
            !email.includes(searchTerm) && 
            !phone.includes(searchTerm)) {
          return false;
        }
      }

      // Filtr statusu
      if (filters.status !== 'all' && client.status !== filters.status) {
        return false;
      }

      // Filtr okresu ostatniego kontaktu
      if (filters.contactPeriod !== 'all') {
        const now = new Date();
        const contactDate = client.lastContactDate;
        
        switch (filters.contactPeriod) {
          case 'last_7_days':
            if (!contactDate || (now - contactDate) > 7 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'last_30_days':
            if (!contactDate || (now - contactDate) > 30 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'last_90_days':
            if (!contactDate || (now - contactDate) > 90 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'no_contact_30':
            if (contactDate && (now - contactDate) <= 30 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'no_contact_90':
            if (contactDate && (now - contactDate) <= 90 * 24 * 60 * 60 * 1000) return false;
            break;
        }
      }

      return true;
    });
  };

  const handleAddClient = async () => {
    try {
      const clientData = {
        ...newClient,
        createdAt: new Date(),
        lastContactDate: null,
        lastPurchaseDate: null,
        totalSpent: 0,
        preferences: newClient.preferences.filter(p => p.trim() !== '')
      };

      await addDoc(collection(db, 'clients'), clientData);

      setSnackbar({
        open: true,
        message: 'Klient został dodany pomyślnie',
        severity: 'success'
      });

      setOpenAddClient(false);
      setNewClient({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        assignedTo: isAdmin ? '' : currentUser?.uid || '',
        status: 'New',
        preferences: [],
        notes: ''
      });

      fetchClients();
    } catch (error) {
      console.error('Błąd podczas dodawania klienta:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas dodawania klienta',
        severity: 'error'
      });
    }
  };

  const handleViewClient = (clientId, clientName) => {
    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    setOpenClientDetails(true);
  };

  const handleAddContact = (clientId, clientName) => {
    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    setOpenAddContact(true);
  };

  const handleAddSale = (clientId, clientName) => {
    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    setOpenAddPurchase(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : 'Nieznany';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VIP': return 'primary';
      case 'Regular': return 'success';
      case 'New': return 'info';
      case 'Inactive': return 'warning';
      case 'Churned': return 'error';
      default: return 'default';
    }
  };

  const renderFilters = () => (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography>Filtry i wyszukiwanie</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Wyszukaj klienta"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              placeholder="Imię, nazwisko, email, telefon..."
            />
          </Grid>

          {isAdmin && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Przypisany do</InputLabel>
                <Select
                  value={filters.assignedTo}
                  onChange={(e) => setFilters({...filters, assignedTo: e.target.value})}
                  label="Przypisany do"
                >
                  <MenuItem value="all">Wszyscy pracownicy</MenuItem>
                  {employees.map(employee => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                label="Status"
              >
                <MenuItem value="all">Wszystkie statusy</MenuItem>
                {Object.entries(CLIENT_STATUSES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Ostatni kontakt</InputLabel>
              <Select
                value={filters.contactPeriod}
                onChange={(e) => setFilters({...filters, contactPeriod: e.target.value})}
                label="Ostatni kontakt"
              >
                <MenuItem value="all">Wszystkie okresy</MenuItem>
                {Object.entries(CONTACT_PERIODS).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddClient(true)}
              fullWidth
            >
              Dodaj klienta
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Zarządzanie klientami
      </Typography>

      {renderFilters()}

      {/* Statystyki */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Łączna liczba klientów
              </Typography>
              <Typography variant="h4">
                {totalClients}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Klienci VIP
              </Typography>
              <Typography variant="h4">
                {clients.filter(c => c.status === 'VIP').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nowi klienci
              </Typography>
              <Typography variant="h4">
                {clients.filter(c => c.status === 'New').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nieaktywni
              </Typography>
              <Typography variant="h4">
                {clients.filter(c => c.status === 'Inactive').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela klientów */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Imię i Nazwisko</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefon</TableCell>
              {isAdmin && <TableCell>Przypisany do</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Ostatni kontakt</TableCell>
              <TableCell>Ostatni zakup</TableCell>
              <TableCell>Łącznie wydane</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Brak klientów spełniających kryteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {`${client.firstName} ${client.lastName}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>{client.phone}</TableCell>
                  {isAdmin && (
                    <TableCell>{getEmployeeName(client.assignedTo)}</TableCell>
                  )}
                  <TableCell>
                    <Chip 
                      label={CLIENT_STATUSES[client.status] || client.status} 
                      color={getStatusColor(client.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {client.lastContactDate ? 
                      format(client.lastContactDate, 'dd.MM.yyyy', { locale: pl }) : 
                      'Brak kontaktu'
                    }
                  </TableCell>
                  <TableCell>
                    {client.lastPurchaseDate ? 
                      format(client.lastPurchaseDate, 'dd.MM.yyyy', { locale: pl }) : 
                      'Brak zakupów'
                    }
                  </TableCell>
                  <TableCell>
                    <strong>{formatPrice(client.totalSpent || 0)}</strong>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Zobacz szczegóły">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewClient(client.id, `${client.firstName} ${client.lastName}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dodaj kontakt">
                        <IconButton 
                          size="small"
                          onClick={() => handleAddContact(client.id, `${client.firstName} ${client.lastName}`)}
                        >
                          <ContactIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dodaj sprzedaż">
                        <IconButton 
                          size="small"
                          onClick={() => handleAddSale(client.id, `${client.firstName} ${client.lastName}`)}
                        >
                          <SalesIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginacja */}
      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Modalne okna */}
      <ClientDetailsModal 
        open={openClientDetails} 
        handleClose={() => setOpenClientDetails(false)} 
        clientId={selectedClientId}
        onClientUpdate={fetchClients}
      />
      
      <AddContactModal 
        open={openAddContact} 
        handleClose={() => setOpenAddContact(false)} 
        clientId={selectedClientId}
        clientName={selectedClientName}
        onContactAdded={() => fetchClients()}
      />
      
      <AddPurchaseModal 
        open={openAddPurchase} 
        handleClose={() => setOpenAddPurchase(false)} 
        clientId={selectedClientId}
        clientName={selectedClientName}
        onPurchaseAdded={() => fetchClients()}
      />
      
      {/* Dialog dodawania klienta */}
      <Dialog open={openAddClient} onClose={() => setOpenAddClient(false)} maxWidth="md" fullWidth>
        <DialogTitle>Dodaj nowego klienta</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Imię"
                value={newClient.firstName}
                onChange={(e) => setNewClient({...newClient, firstName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nazwisko"
                value={newClient.lastName}
                onChange={(e) => setNewClient({...newClient, lastName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={newClient.phone}
                onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
              />
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Przypisz do pracownika</InputLabel>
                  <Select
                    value={newClient.assignedTo}
                    onChange={(e) => setNewClient({...newClient, assignedTo: e.target.value})}
                    label="Przypisz do pracownika"
                  >
                    {employees.map(employee => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newClient.status}
                  onChange={(e) => setNewClient({...newClient, status: e.target.value})}
                  label="Status"
                >
                  {Object.entries(CLIENT_STATUSES).map(([key, value]) => (
                    <MenuItem key={key} value={key}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notatki"
                multiline
                rows={3}
                value={newClient.notes}
                onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                placeholder="Dodatkowe informacje o kliencie..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddClient(false)}>Anuluj</Button>
          <Button 
            onClick={handleAddClient} 
            variant="contained"
            disabled={!newClient.firstName || !newClient.lastName}
          >
            Dodaj klienta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;
